/**
 * GDriveTeamPickerModal
 *
 * Fixes the "no team context" blocker (docs/features/gdrive-folder-scoping/SPEC.md
 * §2/§5.1): when a user connects Google Drive from a team-agnostic route
 * (Account Settings → Channels) with no resolvable team — e.g. agency staff
 * with no home team and no active God Mode session — the connect route would
 * otherwise 400 with `NO_TEAM_CONTEXT`.
 *
 * This modal asks the user which brand workspace the connection is for
 * *before* starting OAuth. It reuses the existing agency workspaces list
 * (`agencyService.getWorkspaces`, already used by AgencyCommandControl /
 * TeamManagement for the God-Mode workspace switcher) as the source of
 * "accessible teams" — there is no dedicated "my accessible teams" endpoint,
 * so this is the closest existing fit for agency staff. Regular users always
 * have a home `teamId` and never see this modal (see `hasTeam` gating at the
 * call site in ChannelSettings.tsx).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agencyService } from '@/services/agency.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { RadioGroup, RadioCard } from '@/components/ui/Radio';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';

interface GDriveTeamPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen team's id once the user confirms. */
  onContinue: (teamId: number) => void;
  isSubmitting?: boolean;
}

export const GDriveTeamPickerModal = ({
  open,
  onOpenChange,
  onContinue,
  isSubmitting = false,
}: GDriveTeamPickerModalProps) => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const agencyId = user?.agencyId ?? null;
  const isAgencyStaff = user?.isAgencyStaff ?? false;
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  const { data: workspaces, isLoading } = useQuery({
    queryKey: teamQueryKeys.agency.workspaces(agencyId),
    queryFn: agencyService.getWorkspaces,
    enabled: open && isAgencyStaff,
  });

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) setSelectedTeamId('');
    onOpenChange(nextOpen);
  };

  const handleContinue = () => {
    const parsed = parseInt(selectedTeamId, 10);
    if (!Number.isNaN(parsed)) {
      onContinue(parsed);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={t('gdrive_team_picker_title', { defaultValue: 'Which brand is this Google Drive connection for?' })}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={() => handleClose(false)}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {t('settings_modal_cancel')}
          </button>
          <Button onClick={handleContinue} disabled={!selectedTeamId || isSubmitting} loading={isSubmitting}>
            {t('gdrive_team_picker_continue', { defaultValue: 'Continue to Google' })}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('gdrive_team_picker_description', {
            defaultValue:
              "Your account isn't currently viewing a specific Brand. Pick which one this Google Drive connection belongs to — you'll choose which folders to share next.",
          })}
        </p>

        {!isAgencyStaff ? (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3">
            <Icon name={ICONS.WARNING} size="sm" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              {t('gdrive_team_picker_no_workspace', {
                defaultValue: "We couldn't find a Brand for your account. Please contact support to get this fixed.",
              })}
            </p>
          </div>
        ) : isLoading ? (
          <div className="py-6 text-center text-sm text-gray-500">
            {t('gdrive_team_picker_loading', { defaultValue: 'Loading brands…' })}
          </div>
        ) : workspaces && workspaces.length > 0 ? (
          <div className="max-h-72 overflow-y-auto pr-1">
            <RadioGroup value={selectedTeamId} onValueChange={setSelectedTeamId}>
              {workspaces.map((ws) => (
                <RadioCard key={ws.id} value={String(ws.id)} label={ws.name} />
              ))}
            </RadioGroup>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3">
            <Icon name={ICONS.WARNING} size="sm" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              {t('gdrive_team_picker_no_workspaces_found', {
                defaultValue: 'No brands found for your Brand Portfolio yet.',
              })}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
